import React, { Dispatch, PropsWithChildren, useContext, useMemo, useReducer } from 'react';
import { v4 as uuid } from 'uuid';

export interface ToastValue {
  id: string;
  text: string;
  createdAt: number;
  isAutoDismiss: boolean;
}

type ToastsContextValue = {
  toasts: ToastValue[];
  createToast: (text: string, isAutoDismiss?: boolean) => void;
  removeToast: (id: string) => void;
};

type State = {
  toasts: ToastValue[];
};

enum Actions {
  CREATE_TOAST = 'CREATE_TOAST',
  REMOVE_TOAST = 'REMOVE_TOAST',
}

type Action =
  | { type: Actions.CREATE_TOAST; payload: ToastValue }
  | { type: Actions.REMOVE_TOAST; payload: string };

const DEFAULT_STATE: State = { toasts: [] };

const reducer = (state: State, action: Action) => {
  const { payload, type } = action;

  switch (type) {
    case Actions.CREATE_TOAST:
      return {
        ...state,
        toasts: [...state.toasts, payload],
      };
    case Actions.REMOVE_TOAST:
      return {
        ...state,
        toasts: state.toasts.filter((toast) => toast.id !== payload),
      };
    default:
      return state;
  }
};

const actions = (dispatch: Dispatch<Action>) => {
  const createToast = (text = '', isAutoDismiss = true): void => {
    dispatch({
      type: Actions.CREATE_TOAST,
      payload: {
        id: uuid(),
        text,
        createdAt: Date.now(),
        isAutoDismiss,
      },
    });
  };

  const removeToast = (id: string): void => {
    dispatch({
      type: Actions.REMOVE_TOAST,
      payload: id,
    });
  };

  return {
    createToast,
    removeToast,
  };
};

const ToastsContext = React.createContext<ToastsContextValue | undefined>(undefined);

export function ToastsContextProvider({ children }: PropsWithChildren) {
  const [{ toasts }, dispatch] = useReducer(reducer, DEFAULT_STATE);

  const value = useMemo<ToastsContextValue>(() => {
    const { createToast, removeToast } = actions(dispatch);
    return {
      toasts,
      createToast,
      removeToast,
    };
  }, [toasts]);

  return <ToastsContext.Provider value={value}>{children}</ToastsContext.Provider>;
}

export const useToasts = () => {
  const context = useContext(ToastsContext);
  if (!context) {
    throw new Error('useToasts hook must be used within a ToastsContextProvider');
  }

  return context;
};
